var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElementUtils, Utils, P, PropertyConverters, Watch, ViewChild, Components, CustomElement } from '@pacem/pacem-core';
import { Components as ComponentsUI } from '@pacem/pacem-ui';
const ANATOMY_URL = '/dist/docs/anatomy.json';
let anatomyIndex = null;
function loadAnatomyIndex() {
    if (Utils.isNull(anatomyIndex)) {
        anatomyIndex = fetch(ANATOMY_URL)
            .then(response => response.ok ? response.json() : { classes: {} })
            .catch(() => ({ classes: {} }));
    }
    return anatomyIndex;
}
function converterName(converter) {
    if (Utils.isNull(converter)) {
        return 'none';
    }
    const source = converter.convert?.toString();
    const entries = Object.entries(PropertyConverters);
    const found = entries.find(([_, value]) => value === converter || value.convert?.toString() === source);
    if (Utils.isNull(found)) {
        return 'custom';
    }
    return found[0] === 'None' ? 'none' : found[0];
}
function inlineHtml(text) {
    const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return escaped
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
}
function paragraphHtml(paragraph) {
    const parts = paragraph.split(/(?<=[:;])\s+-\s+/);
    const intro = inlineHtml(parts[0]);
    if (parts.length === 1) {
        return `<p>${intro}</p>`;
    }
    const items = parts.slice(1).map(item => `<li>${inlineHtml(item)}</li>`);
    return `<p>${intro}</p><ul>${items.join('')}</ul>`;
}
function docHtml(doc) {
    const paragraphs = (doc ?? '')
        .split(/\n\s*\n/)
        .map(p => p.replace(/\s*\n\s*/g, ' ').trim())
        .filter(p => p.length > 0);
    return paragraphs.map(paragraphHtml).join('');
}
function docOf(candidates, watched) {
    let best = null, bestScore = -1;
    for (let candidate of candidates ?? []) {
        const documented = Object.keys(candidate.properties);
        const score = watched.filter(name => documented.indexOf(name) >= 0).length;
        if (score > bestScore) {
            best = candidate;
            bestScore = score;
        }
    }
    return best;
}
let PacemCustomElementDescriptorElement = class PacemCustomElementDescriptorElement extends Components.PacemElement {
    constructor() {
        super(...arguments);
        this.view = { properties: [], methods: [], events: [] };
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'target':
                this._render();
                break;
            case 'anatomy':
            case 'showInherited':
                this._filter();
                break;
        }
    }
    _filter() {
        const anatomy = this.anatomy;
        if (Utils.isNull(anatomy)) {
            return;
        }
        const inherited = this.showInherited === true;
        const shown = (member) => member.own || inherited;
        this.view = {
            properties: anatomy.properties.filter(shown),
            methods: anatomy.methods.filter(shown),
            events: anatomy.events.filter(shown)
        };
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._render();
    }
    async _render() {
        const target = this.target;
        if (Utils.isNull(target)) {
            return;
        }
        const index = await loadAnatomyIndex();
        const anatomy = { type: target.constructor.name, extends: '', summary: '', properties: [], methods: [], events: [] };
        let type = target.constructor;
        let own = true;
        while (!Utils.isNull(type) && type !== HTMLElement && type !== Function.prototype) {
            this._describe(anatomy, type, index.classes[type.name], own);
            own = false;
            type = Object.getPrototypeOf(type);
        }
        const members = [...anatomy.properties, ...anatomy.methods, ...anatomy.events];
        const hasOwn = members.some(m => m.own);
        if (!hasOwn) {
            this.showInherited = true;
        }
        this.anatomy = anatomy;
    }
    _describe(anatomy, type, candidates, own) {
        const watched = CustomElementUtils.getWatchedProperties(type, false);
        const watchedNames = watched.map(w => w.name);
        const doc = docOf(candidates, watchedNames);
        const owner = type.name;
        if (own) {
            anatomy.extends = doc?.extends ?? Object.getPrototypeOf(type)?.name ?? '';
            anatomy.summary = docHtml(doc?.summary);
        }
        const properties = watched.map(w => {
            const documented = doc?.properties[w.name];
            const attribute = CustomElementUtils.camelToKebab(w.name);
            const converter = converterName(w.config?.converter);
            const html = docHtml(documented?.doc);
            return { name: w.name, attribute, type: documented?.type ?? '', converter, doc: html, owner, own };
        });
        properties.sort((a, b) => a.name.localeCompare(b.name));
        Array.prototype.push.apply(anatomy.properties, properties);
        if (Utils.isNull(doc)) {
            return;
        }
        const methods = doc.methods.map(m => ({ signature: m.signature, doc: docHtml(m.doc), owner, own }));
        Array.prototype.push.apply(anatomy.methods, methods);
        const events = doc.fires.map(f => ({ name: f.name, type: f.type, doc: docHtml(f.doc), owner, own }));
        Array.prototype.push.apply(anatomy.events, events);
    }
};
__decorate([
    Watch({ converter: PropertyConverters.Element })
], PacemCustomElementDescriptorElement.prototype, "target", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemCustomElementDescriptorElement.prototype, "showInherited", void 0);
__decorate([
    Watch()
], PacemCustomElementDescriptorElement.prototype, "anatomy", void 0);
__decorate([
    Watch()
], PacemCustomElementDescriptorElement.prototype, "view", void 0);
__decorate([
    ViewChild(P + "-media-query")
], PacemCustomElementDescriptorElement.prototype, "_mq", void 0);
PacemCustomElementDescriptorElement = __decorate([
    CustomElement({
        tagName: 'pacemjs-descriptor', shadow: false,
        template: `<${P}-media-query query="${ComponentsUI.UI.MEDIAQUERY_LG}"></${P}-media-query>
<h2 class="anatomy-title"> 
<${P}-text text="{{ :host.anatomy.type }}" anatomy-type></${P}-text> <span class="text-gray type-std font-size-big">Type</span></h2>
<div class="anatomy-heading display-flex flex-wrap flex-baseline gap-2">
    <${P}-span class="anatomy-extends" hide="{{ !:host.anatomy.extends }}" text="{{ 'extends ' + :host.anatomy.extends }}"></${P}-span>
    <div class="flex-auto"></div>
    <${P}-checkbox value="{{ :host.showInherited, twoway }}" true-value="true" false-value="false" caption="show inherited"
                   css-class="{{ {'checkbox-naked': ::_mq.isMatch } }}"></${P}-checkbox>
</div>
<${P}-span class="anatomy-summary display-block" content="{{ :host.anatomy.summary.replaceAll(/\\$\\{P\\}/g, 'pacem') }}"></${P}-span>

<${P}-if class="anatomy-section anatomy-properties display-block" match="{{ :host.view.properties.length > 0 }}">
    <h4>Properties</h4>
    <${P}-repeater datasource="{{ :host.view.properties }}">
        <div class="anatomy-list">
            <template>
                <div class="anatomy-member">
                    <div class="anatomy-facets display-flex flex-wrap flex-baseline gap-1">
                        <code class="anatomy-name"><${P}-text text="{{ ^item.name }}"></${P}-text></code>
                        <${P}-span class="anatomy-attribute" hide="{{ ^item.attribute === ^item.name }}" text="{{ '[' + ^item.attribute + ']' }}"></${P}-span>
                        <${P}-span class="anatomy-kind" text="{{ ^item.type }}" hide="{{ !^item.type }}"></${P}-span>
                        <${P}-span class="anatomy-converter pacem-chip" text="{{ ^item.converter }}" title="attribute converter"></${P}-span>
                        <${P}-span class="anatomy-owner" hide="{{ ^item.own }}" text="{{ ^item.owner }}"></${P}-span>
                    </div>
                    <${P}-span class="anatomy-doc" content="{{ ^item.doc }}" hide="{{ !^item.doc }}"></${P}-span>
                </div>
            </template>
        </div>
    </${P}-repeater>
</${P}-if>

<${P}-if class="anatomy-section anatomy-methods display-block" match="{{ :host.view.methods.length > 0 }}">
    <h4>Methods</h4>
    <${P}-repeater datasource="{{ :host.view.methods }}">
        <div class="anatomy-list">
            <template>
                <div class="anatomy-member">
                    <div class="anatomy-facets display-flex flex-wrap flex-baseline gap-1">
                        <code class="anatomy-name"><${P}-text text="{{ ^item.signature }}"></${P}-text></code>
                        <${P}-span class="anatomy-owner" hide="{{ ^item.own }}" text="{{ ^item.owner }}"></${P}-span>
                    </div>
                    <${P}-span class="anatomy-doc" content="{{ ^item.doc }}" hide="{{ !^item.doc }}"></${P}-span>
                </div>
            </template>
        </div>
    </${P}-repeater>
</${P}-if>

<${P}-if class="anatomy-section anatomy-events display-block" match="{{ :host.view.events.length > 0 }}">
    <h4>Events</h4>
    <${P}-repeater datasource="{{ :host.view.events }}">
        <div class="anatomy-list">
            <template>
                <div class="anatomy-member">
                    <div class="anatomy-facets display-flex flex-wrap flex-baseline gap-1">
                        <code class="anatomy-name"><${P}-text text="{{ ^item.name }}"></${P}-text></code>
                        <${P}-span class="anatomy-kind" text="{{ ^item.type }}" hide="{{ !^item.type }}"></${P}-span>
                        <${P}-span class="anatomy-owner" hide="{{ ^item.own }}" text="{{ ^item.owner }}"></${P}-span>
                    </div>
                    <${P}-span class="anatomy-doc" content="{{ ^item.doc }}" hide="{{ !^item.doc }}"></${P}-span>
                </div>
            </template>
        </div>
    </${P}-repeater>
</${P}-if>
<!-- hack to trigger FontAwesome import. To be revisited in a future (along with demo css tweaks) --><${P}-icon icon="fas fa-dice-6" hide="true"></${P}-icon>`
    })
], PacemCustomElementDescriptorElement);
export { PacemCustomElementDescriptorElement };
